/**
 * PrepararProyecto.jsx
 * Solo datos del proyecto — las tareas se agregan después desde el Kanban.
 * Modo A — cotizacion != null : prellenado desde cotización aprobada
 * Modo B — cotizacion == null : proyecto directo sin cotización
 */
import { useState } from "react";
import { supabase } from "../lib/supabase";
import {
  Check, X, Loader, AlertCircle, FolderOpen, Mail, MailX,
} from "lucide-react";

const N8N_PROYECTO_CREADO = "https://tuaplicacion.app.n8n.cloud/webhook/proyecto-creado";
const APP_URL             = "https://app.tuaplicacion.cl";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
};

const iStyle = {
  width:"100%", padding:"8px 12px", borderRadius:7,
  border:`1px solid ${C.border}`, background:C.white,
  color:C.text, fontSize:14, outline:"none",
  transition:"border-color .15s, box-shadow .15s",
  fontFamily:"'DM Sans',sans-serif",
};

export default function PrepararProyecto({ cotizacion, onConfirm, onCancel }) {
  const modoDirecto = !cotizacion;

  const [nombre,       setNombre]       = useState(modoDirecto ? "" : `Proyecto ${cotizacion.cliente}`);
  const [cliente,      setCliente]      = useState(modoDirecto ? "" : cotizacion.cliente);
  const [email,        setEmail]        = useState(modoDirecto ? "" : cotizacion.email);
  const [telefono,     setTelefono]     = useState("");
  const [rut,          setRut]          = useState(modoDirecto ? "" : (cotizacion?.rut || ""));
  const [fechaInicio,  setFechaInicio]  = useState(new Date().toISOString().slice(0,10));
  const [fechaEntrega, setFechaEntrega] = useState(
    new Date(Date.now() + 30*864e5).toISOString().slice(0,10)
  );
  const [notas,      setNotas]      = useState("");
  const [enviarLink, setEnviarLink] = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState(null);

  const notify = (msg, type="ok") => {
    setToast({msg, type});
    setTimeout(() => setToast(null), 3500);
  };

  const confirmar = async () => {
    if (!nombre.trim())  { notify("El nombre del proyecto es requerido", "err"); return; }
    if (!cliente.trim()) { notify("El cliente es requerido", "err"); return; }
    if (!email.trim())   { notify("El email es requerido", "err"); return; }
    if (!fechaEntrega)   { notify("La fecha de entrega es requerida", "err"); return; }

    setSaving(true);

    // Crear proyecto sin tareas — se agregan desde el kanban
    const { data, error } = await supabase.rpc("crear_proyecto_desde_cotizacion", {
      p_cotizacion_id: cotizacion?.id || null,
      p_nombre:        nombre.trim(),
      p_fecha_entrega: fechaEntrega,
      p_notas:         notas.trim(),
      p_tareas:        (cotizacion?.items || []).map((item, idx) => ({
        descripcion:     item.desc,
        tipo:            item.tipo,
        horas_estimadas: item.horas,
        estado:          "Por hacer",
        orden:           idx + 1,
      })),
    });

    if (error || (data && !data.success)) {
      notify(data?.error || "Error al crear el proyecto", "err");
      console.error(error || data);
      setSaving(false); return;
    }

    // Campos adicionales
    await supabase.from("proyectos").update({
      telefono:     telefono.trim(),
      rut:          rut.trim(),
      fecha_inicio: fechaInicio || null,
    }).eq("id", data.proyecto_id);

    // Email al cliente solo si el toggle está activo
    if (enviarLink) {
      try {
        const { data: pData } = await supabase
          .from("proyectos").select("token").eq("id", data.proyecto_id).single();
        await fetch(N8N_PROYECTO_CREADO, {
          method: "POST", headers: {"Content-Type":"application/json"},
          body: JSON.stringify({
            proyecto_id:   data.proyecto_id,
            nombre:        nombre.trim(),
            cliente:       cliente.trim(),
            email_cliente: email.trim(),
            fecha_entrega: fechaEntrega,
            total_tareas:  0,
            link_proyecto: `${APP_URL}/?ptoken=${pData.token}`,
          }),
        });
      } catch(e) { console.warn("n8n webhook no disponible:", e); }
    }

    setSaving(false);
    onConfirm(data.proyecto_id);
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9990,
      background:"rgba(15,23,42,0.5)", backdropFilter:"blur(3px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"24px 16px",
    }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinning{animation:spin 1s linear infinite}
        input:focus,textarea:focus,select:focus{outline:none;border-color:#29abe2!important;box-shadow:0 0 0 3px #29abe215}
      `}</style>

      {toast && (
        <div style={{position:"fixed", top:20, right:20, zIndex:9999,
          display:"flex", alignItems:"center", gap:8, background:C.white,
          border:`1px solid ${toast.type==="err"?C.err:C.ok}40`,
          borderLeft:`3px solid ${toast.type==="err"?C.err:C.ok}`,
          borderRadius:8, padding:"11px 16px", fontSize:14,
          color:toast.type==="err"?C.err:C.ok,
          boxShadow:"0 4px 16px #00000014"}}>
          {toast.type==="err" ? <AlertCircle size={15}/> : <Check size={15}/>}
          {toast.msg}
        </div>
      )}

      <div style={{
        background:C.white, borderRadius:14, width:"100%", maxWidth:560,
        boxShadow:"0 24px 80px #00000020", border:`1px solid ${C.border}`,
        animation:"fadeUp .2s ease",
      }}>

        {/* Header */}
        <div style={{padding:"20px 24px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <div style={{display:"flex", alignItems:"center", gap:12}}>
            <div style={{width:36, height:36, borderRadius:9, background:C.accentL,
              display:"flex", alignItems:"center", justifyContent:"center"}}>
              <FolderOpen size={18} color={C.accent}/>
            </div>
            <div>
              <div style={{fontWeight:700, fontSize:16, color:C.text}}>
                {modoDirecto ? "Nuevo proyecto" : "Crear proyecto"}
              </div>
              <div style={{fontSize:12, color:C.sub, marginTop:2}}>
                {modoDirecto
                  ? "Las tareas se agregan desde el kanban"
                  : <>Desde cotización <strong style={{color:C.text}}>{cotizacion.id}</strong> · {cotizacion.cliente}</>}
              </div>
            </div>
          </div>
          <button onClick={onCancel}
            style={{background:"transparent", border:"none", cursor:"pointer",
              color:C.light, padding:4, borderRadius:6, display:"flex"}}
            onMouseEnter={e => e.currentTarget.style.color=C.text}
            onMouseLeave={e => e.currentTarget.style.color=C.light}>
            <X size={18}/>
          </button>
        </div>

        <div style={{padding:"22px 24px"}}>

          {/* Nombre + fecha entrega */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14}}>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>Nombre del proyecto *</label>
              <input value={nombre} onChange={e => setNombre(e.target.value)}
                placeholder="Ej: Portal E-commerce 2026" style={iStyle}/>
            </div>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>Fecha de entrega *</label>
              <input type="date" value={fechaEntrega}
                onChange={e => setFechaEntrega(e.target.value)} style={iStyle}/>
            </div>
          </div>

          {/* Fecha inicio */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block", fontSize:12, fontWeight:600,
              color:C.sub, marginBottom:5}}>Fecha de inicio</label>
            <input type="date" value={fechaInicio}
              onChange={e => setFechaInicio(e.target.value)} style={iStyle}/>
          </div>

          {/* Separador */}
          <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:14}}>
            <div style={{flex:1, height:1, background:C.border}}/>
            <span style={{fontSize:11, fontWeight:600, color:C.sub,
              letterSpacing:.5, textTransform:"uppercase"}}>
              Datos del cliente
            </span>
            <div style={{flex:1, height:1, background:C.border}}/>
          </div>

          {/* Datos cliente */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14}}>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>
                {modoDirecto ? "Empresa / Cliente *" : "Cliente"}
              </label>
              <input value={cliente} onChange={e => setCliente(e.target.value)}
                placeholder="Retail Digital SpA"
                style={{...iStyle,
                  background: modoDirecto ? C.white : "#f8fafc",
                  color:      modoDirecto ? C.text  : C.sub}}
                readOnly={!modoDirecto}/>
            </div>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>
                {modoDirecto ? "Email *" : "Email"}
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="cliente@empresa.cl"
                style={{...iStyle,
                  background: modoDirecto ? C.white : "#f8fafc",
                  color:      modoDirecto ? C.text  : C.sub}}
                readOnly={!modoDirecto}/>
            </div>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>Teléfono</label>
              <input value={telefono} onChange={e => setTelefono(e.target.value)}
                placeholder="+56 9 1234 5678" style={iStyle}/>
            </div>
            <div>
              <label style={{display:"block", fontSize:12, fontWeight:600,
                color:C.sub, marginBottom:5}}>RUT</label>
              <input value={rut} onChange={e => setRut(e.target.value)}
                placeholder="76.543.210-9" style={iStyle}/>
            </div>
          </div>

          {/* Notas */}
          <div style={{marginBottom:16}}>
            <label style={{display:"block", fontSize:12, fontWeight:600,
              color:C.sub, marginBottom:5}}>Notas internas</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)}
              rows={2} placeholder="Ej: Cliente prefiere reuniones los jueves..."
              style={{...iStyle, resize:"vertical"}}/>
          </div>

          {/* Toggle email */}
          <div style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"13px 16px", borderRadius:10, marginBottom:22,
            background: enviarLink ? C.accentL : "#f1f5f9",
            border:`1px solid ${enviarLink ? C.accent+"40" : C.border}`,
            cursor:"pointer", transition:"all .15s",
          }}
            onClick={() => setEnviarLink(!enviarLink)}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              {enviarLink
                ? <Mail size={16} color={C.accent}/>
                : <MailX size={16} color={C.sub}/>}
              <div>
                <div style={{fontWeight:600, fontSize:13,
                  color: enviarLink ? C.accent : C.sub}}>
                  {enviarLink ? "Enviar email al cliente" : "No enviar email al cliente"}
                </div>
                <div style={{fontSize:11, color:C.light, marginTop:2}}>
                  {enviarLink
                    ? `Se enviará el link del proyecto a ${email || "el cliente"}`
                    : "El proyecto se creará sin notificar al cliente"}
                </div>
              </div>
            </div>
            <div style={{
              width:38, height:22, borderRadius:11,
              background: enviarLink ? C.accent : C.light,
              position:"relative", flexShrink:0, transition:"background .2s",
            }}>
              <div style={{
                position:"absolute", top:3,
                left: enviarLink ? 18 : 3,
                width:16, height:16, borderRadius:"50%",
                background:C.white, transition:"left .2s",
                boxShadow:"0 1px 3px #00000020",
              }}/>
            </div>
          </div>

          {/* Acciones */}
          <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
            <button onClick={onCancel}
              style={{padding:"9px 22px", borderRadius:8,
                border:`1px solid ${C.border}`, background:"transparent",
                color:C.sub, fontSize:14, cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              Cancelar
            </button>
            <button onClick={confirmar} disabled={saving}
              style={{display:"flex", alignItems:"center", gap:7,
                padding:"9px 26px", borderRadius:8, border:"none",
                background:saving?"#94a3b8":C.accent, color:"#fff",
                fontWeight:600, fontSize:14,
                cursor:saving?"not-allowed":"pointer",
                fontFamily:"'DM Sans',sans-serif",
                boxShadow:saving?"none":`0 4px 12px ${C.accent}40`,
                transition:"background .15s"}}
              onMouseEnter={e => { if(!saving) e.currentTarget.style.background="#1a9fd4"; }}
              onMouseLeave={e => { if(!saving) e.currentTarget.style.background=C.accent; }}>
              {saving
                ? <><Loader size={14} className="spinning"/>Creando...</>
                : <><FolderOpen size={15}/>Crear proyecto</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
